import React from 'react';
import styled, { css } from 'styled-components';

import { spacing } from 'ui/theme/utils';

export const Tooltip = React.forwardRef(
  (
    {
      children,
      content,
      wrap = true,
      fixed = true,
      wrapperProps,
      contentProps,
      onVisibilityChange,
      placement: initialPlacement = 'bottom',
      ...rest
    },
    ref
  ) => {
    const [position, setPosition] = React.useState({ top: 0, bottom: 0, left: 0 });
    const [placement, setPlacement] = React.useState(initialPlacement);
    const [visible, setVisible] = React.useState(false);
    const wrapperRef = React.useRef();
    const contentRef = React.useRef();

    // allow us to call changeVisibility from parent
    React.useImperativeHandle(ref, () => ({
      changeVisibility,
    }));

    const changeVisibility = (value) => {
      setVisible(value);
      if (onVisibilityChange) {
        onVisibilityChange(value);
      }
    };

    const handleMouseEnter = () => {
      if (fixed && wrapperRef.current) {
        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const contentRect = contentRef.current.getBoundingClientRect();
        const pos = {
          top: wrapperRect.y + wrapperRect.height,
          bottom: window.innerHeight - wrapperRect.y,
          left: wrapperRect.x + wrapperRect.width / 2 - contentRect.width / 2,
        };
        if (pos.top < 0) pos.top = 10;
        if (pos.left < 0) pos.left = 10;
        if (pos.left + contentRect.width > window.innerWidth) pos.right = 10;
        if (pos.top + contentRect.height > window.innerHeight) setPlacement('top');
        else setPlacement(initialPlacement);
        setPosition(pos);
      }
      changeVisibility(true);
    };

    const handleMouseLeave = () => {
      changeVisibility(false);
    };

    return wrap ? (
      <TooltipWrapper
        ref={wrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...wrapperProps}
      >
        {children}
        <TooltipContentBox
          ref={contentRef}
          show={visible}
          position={position}
          placement={placement}
          fixed={fixed}
          {...rest}
        >
          <TooltipContent {...contentProps}>{content}</TooltipContent>
        </TooltipContentBox>
      </TooltipWrapper>
    ) : (
      children
    );
  }
);

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
  ${({ float }) => float && `float: ${float};`}
  ${(props) => spacing(props)}
`;

const TooltipContentBox = styled.span`
  ${({ show }) =>
    show
      ? `
    visibility: visible;
    opacity: 1;
  `
      : `
    visibility: hidden;
    opacity: 0;
  `}
  transition: opacity 300ms ease-in-out;
  min-width: ${({ width = '120px' }) => width};
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth};`}
  z-index: 1;
  overflow: ${({ overflow = 'initial' }) => overflow};
  ${({ fixed }) =>
    fixed
      ? css`
          position: fixed;
          ${({ position: { left = 0, right } }) =>
            right
              ? `
            right: ${right}px;
          `
              : `
            left: ${left}px;
          `}
          ${({ placement = 'bottom' }) => {
            switch (placement) {
              case 'top':
                return css`
                  bottom: ${({ position: { bottom = 0 } }) => `${bottom}px`};
                  padding-bottom: 5px;
                `;
              case 'bottom':
                return css`
                  top: ${({ position: { top = 0 } }) => `${top}px`};
                  padding-top: 5px;
                `;
            }
          }}
        `
      : css`
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          ${({ placement = 'bottom' }) => {
            switch (placement) {
              case 'top':
                return css`
                  bottom: 100%;
                  padding-bottom: 5px;
                `;
              case 'bottom':
                return css`
                  top: 100%;
                  padding-top: 5px;
                `;
            }
          }}
        `}
`;

const TooltipContent = styled.div`
  background-color: ${({ theme, bg = 'white' }) => theme.colors[bg] || bg};
  color: ${({ theme, color = 'grey' }) => theme.colors[color] || color};
  font-size: 14px;
  text-align: center;
  line-height: 24px;
  border-radius: 6px;
  padding: 5px;
  filter: drop-shadow(0px 3px 26px rgba(0, 0, 0, 0.16));
  ${(props) => spacing(props)}
`;
